import { type LDContextCommon, LDProvider, type ProviderConfig, useLDClient } from 'launchdarkly-react-client-sdk';
import React, { type PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { useTeamSwitcher } from '@sine/sine-team-switcher';

import teamSwitcherService from '../../services/team/teamSwitcher.service';

export interface LaunchDarklyContextType {
  updateContext: (newContext?: LDContextCommon) => void;
}

const defaultLaunchDarklyContext: LaunchDarklyContextType = {
  updateContext: () => undefined,
};

const LaunchDarklyContext = createContext(defaultLaunchDarklyContext);

function LDTeamSwitcher({ children }: PropsWithChildren) {
  const ldClient = useLDClient();
  const { currentTeam } = useTeamSwitcher();

  const defaultContextValue = useMemo(() => {
    if (!currentTeam) {
      return;
    }

    return {
      user: {
        key: currentTeam.id,
        name: currentTeam.name,
      },
      team: { key: currentTeam.id, name: currentTeam.name },
    };
  }, [currentTeam]);

  const updateContext = useCallback(
    (newContext?: LDContextCommon) => {
      if (ldClient && defaultContextValue) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        ldClient.identify({
          kind: 'multi',
          ...defaultContextValue,
          ...newContext,
        });
      }
    },
    [defaultContextValue, ldClient],
  );

  useEffect(() => {
    // re-initialise Launch Darkly when users switch teams
    updateContext();
  }, [updateContext]);

  const value = useMemo<LaunchDarklyContextType>(
    () => ({
      updateContext,
    }),
    [updateContext],
  );

  return <LaunchDarklyContext.Provider value={value}>{children}</LaunchDarklyContext.Provider>;
}

export default function LaunchDarklyProvider({ children }: PropsWithChildren<unknown>) {
  const currentTeam = teamSwitcherService.getCurrentTeam();

  const props = useMemo<ProviderConfig>(
    () => ({
      clientSideID: window.env.LAUNCH_DARKLY_CLIENT_ID,
      context: {
        kind: 'user',
        key: currentTeam?.id,
        name: currentTeam?.name,
      },
      options: {
        logger: {
          debug: (message: string) => {
            // eslint-disable-next-line no-console
            console.log(message);
          },
          info: (message: string) => {
            // eslint-disable-next-line no-console
            console.log(message);
          },
          warn: (message: string) => {
            // eslint-disable-next-line no-console
            console.log(message);
          },
          error: (message: string) => {
            // eslint-disable-next-line no-console
            console.log(message);
          },
        },
      },
    }),
    [currentTeam],
  );

  return (
    <LDProvider {...props}>
      <LDTeamSwitcher>{children}</LDTeamSwitcher>
    </LDProvider>
  );
}

export function useLaunchDarkly() {
  return useContext(LaunchDarklyContext);
}
